import React, { useState } from 'react';
import { useAudience } from '../context/AudienceContext';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  Baby,
  Search,
  Thermometer,
  Clock,
  AlertTriangle,
  Users,
  Pill,
  ChevronRight,
  ChevronDown,
  X,
  Sparkles,
  Activity,
  Heart,
  Eye,
  Droplet
} from 'lucide-react';

// Data for childhood eruptive diseases
const eruptiveDiseases = [
  {
    id: 'rougeole',
    name: 'Rougeole',
    description: 'Infection virale par le virus de la rougeole (paramyxovirus)',
    icon: '🔴',
    color: 'text-red-400 bg-red-400/10 border-red-400/30',
    incubation: '8 à 12 jours',
    contagion: 'Très contagieux 5 jours avant et après le début de l\'éruption',
    symptoms: [
      'Malaise général, fatigue, irritabilité',
      'Fièvre élevée',
      'Rhinorrhée, toux',
      'Yeux rouges, larmoyants, photophobie',
      'Signe de Koplik (pathognomonique)'
    ],
    rash: {
      type: 'Maculo-papulaire',
      description: 'Éruption généralisée débutant sur le front et régions rétro-auriculaires, puis s\'étendant au visage, tronc et membres. Disparaît en 5-6 jours avec brunissement et desquamation furfuracée. Taches maculo-papuleuses rouges séparées par peau saine.',
      location: 'Visage → Tronc → Membres'
    },
    complications: [
      'Otite',
      'Convulsions',
      'Diarrhée',
      'Pneumonie',
      'Laryngo-trachéo-bronchite',
      'Encéphalite',
      'Panencéphalite sclérosante subaiguë'
    ],
    treatment: 'Traitement symptomatique. Vitamine A si facteur de risque.',
    eviction: 'Éviction scolaire au moins 4 jours après le début de l\'éruption',
    severity: 'high'
  },
  {
    id: 'varicelle',
    name: 'Varicelle',
    description: 'Infection par le virus varicelle-zona (VZV)',
    icon: '💧',
    color: 'text-cyan-400 bg-cyan-400/10 border-cyan-400/30',
    incubation: 'Environ 15 jours',
    contagion: 'Très contagieux 5 jours avant l\'éruption jusqu\'au dessèchement des lésions',
    symptoms: [
      'État général conservé',
      'Fièvre modérée',
      'Prurit intense',
      'Conjonctives normales'
    ],
    rash: {
      type: 'Vésiculaire',
      description: 'Lésions maculo-papuleuses puis vésiculeuses et croûteuses. Vésicules à liquide clair ou jaunâtre sur peau saine ou érythémateuse. Dans une même zone, lésions d\'âges différents.',
      location: 'Tête et tronc → Membres'
    },
    complications: [
      'Impétigo',
      'Cellulite',
      'Éruption scarlatiniforme',
      'Pneumonie',
      'Hépatite',
      'Ataxie cérébelleuse',
      'Encéphalite',
      'Syndrome de Reye'
    ],
    treatment: 'Traitement symptomatique. Aciclovir si facteurs de risque.',
    eviction: 'Pendant la phase vésiculeuse ou selon réglementation sanitaire',
    severity: 'medium'
  },
  {
    id: 'scarlatine',
    name: 'Scarlatine',
    description: 'Infection par Streptococcus β-hémolytique groupe A',
    icon: '🔥',
    color: 'text-orange-400 bg-orange-400/10 border-orange-400/30',
    incubation: '2 à 5 jours',
    contagion: 'Contagiosité maximale pendant la phase aiguë',
    symptoms: [
      'État général conservé',
      'Fièvre élevée',
      'Angine, dysphagie',
      'Adénopathies cervicales',
      'Douleurs abdominales',
      'Nausées, vomissements',
      'Signe de Pastia (plis cutanés)'
    ],
    rash: {
      type: 'Érythémateux',
      description: 'Peau rouge et rugueuse comme papier de verre. Érythème généralisé prédominant sur le tronc antérieur. Pâleur péribuccale. Langue framboisée. Éruption plus foncée dans les plis (signe de Pastia).',
      location: 'Tronc → Généralisé (respect face)'
    },
    complications: [
      'Abcès péri-amygdalien',
      'Rhumatisme articulaire aigu (RAA)',
      'Glomérulonéphrite aiguë post-streptococcique'
    ],
    treatment: 'Antibiothérapie orale (Pénicilline V ou Amoxicilline)',
    eviction: 'Éviction jusqu\'à guérison (moins de risque après 24h d\'antibiothérapie)',
    severity: 'medium'
  },
  {
    id: 'rubeole',
    name: 'Rubéole',
    description: 'Infection virale par le virus de la rubéole (togavirus)',
    icon: '🌸',
    color: 'text-pink-400 bg-pink-400/10 border-pink-400/30',
    incubation: '14 à 21 jours (moyenne 17 jours)',
    contagion: '7 jours avant à 7 jours après le début de l\'éruption',
    symptoms: [
      'État général conservé',
      'Peu ou pas de fièvre',
      'Adénopathies sous-occipitales et rétro-auriculaires',
      'Arthralgies distales (adolescents/adultes)',
      'Conjonctives normales',
      '⚠️ Risque tératogène'
    ],
    rash: {
      type: 'Maculo-papulaire',
      description: 'Éruption débutant au visage puis tronc et membres. Macules roses ou rouges espacées par peau saine. Éruption non prurigineuse disparaissant en 3-4 jours. Parfois petites taches rouges sur le palais mou (signe de Forschheimer).',
      location: 'Visage → Tronc → Membres'
    },
    complications: [
      'Bénigne chez l\'enfant normal',
      'Rarement: encéphalite, purpura, anémie hémolytique, myocardite'
    ],
    treatment: 'Traitement symptomatique',
    eviction: 'Éviction scolaire 1 semaine (période contagieuse)',
    severity: 'low',
    warning: 'DANGER pour les femmes enceintes non immunisées'
  },
  {
    id: 'roseole',
    name: 'Roséole (6ème maladie)',
    altName: 'Érythème subit',
    description: 'Infection par l\'Herpèsvirus humain 6B (HHV-6B)',
    icon: '🌹',
    color: 'text-rose-400 bg-rose-400/10 border-rose-400/30',
    incubation: '5 à 15 jours',
    contagion: 'Faible contagiosité pendant la phase fébrile',
    symptoms: [
      'État général conservé',
      'Fièvre élevée pendant 3 à 5 jours',
      'Irritabilité',
      'Tympans congestionnés',
      'Anorexie, diarrhée',
      'Adénopathies cervicales postérieures',
      'Parfois bombement de la fontanelle antérieure',
      '"Fièvre de 3 jours" puis éruption'
    ],
    rash: {
      type: 'Maculo-papulaire',
      description: 'Macules rosées sur le tronc (abdomen, région lombaire) et visage (front). Éruption apparaissant 24-48h après normalisation de la température. Disparaît en moins de 3 jours sans desquamation. Parfois taches de Nagayama sur le palais mou.',
      location: 'Tronc (abdomen) → Visage'
    },
    complications: [
      'Convulsions fébriles',
      'Rarement: hypertension intracrânienne bénigne, méningo-encéphalite, hépatite, myocardite'
    ],
    treatment: 'Traitement symptomatique',
    eviction: 'Repos à domicile souhaitable pendant la phase fébrile',
    severity: 'low'
  },
  {
    id: 'cinquieme-maladie',
    name: '5ème Maladie',
    altName: 'Érythème infectieux',
    description: 'Infection par le Parvovirus B19',
    icon: '🦋',
    color: 'text-purple-400 bg-purple-400/10 border-purple-400/30',
    incubation: 'Environ 14 jours',
    contagion: '1 à 5 jours AVANT l\'éruption. Non contagieux pendant l\'éruption',
    symptoms: [
      'État général conservé',
      'Pas de fièvre',
      'Parfois prurit cutané',
      'Arthralgies (adultes)',
      'Conjonctives normales'
    ],
    rash: {
      type: 'Réticulé',
      description: 'Érythème en dentelle ou broderie. Initialement joues rouges ("joues giflées"), puis éruption réticulée symétrique sur avant-bras et cuisses. Parfois petites macules roses sur le thorax. Évolution en éclipses sur plusieurs jours/semaines. Exacerbé par pression, chaleur, soleil, exercice.',
      location: 'Joues → Avant-bras, cuisses'
    },
    complications: [
      'Crise aplasique chez sujets à risque (anémie chronique, immunodéprimés)'
    ],
    treatment: 'Traitement symptomatique',
    eviction: 'Non indiquée (non contagieux pendant l\'éruption)',
    severity: 'low',
    warning: 'Risque d\'anasarque fœto-placentaire chez la femme enceinte'
  },
  {
    id: 'mains-pieds-bouche',
    name: 'Syndrome Mains-Pieds-Bouche',
    description: 'Infection par entérovirus (Coxsackie A16, Entérovirus 71)',
    icon: '✋',
    color: 'text-amber-400 bg-amber-400/10 border-amber-400/30',
    incubation: '3 à 6 jours',
    contagion: 'Faible contagiosité (environ 10 jours)',
    symptoms: [
      'État général conservé',
      'Fièvre modérée',
      'Dysphagie, anorexie',
      'Conjonctives normales'
    ],
    rash: {
      type: 'Vésiculaire',
      description: 'Vésicules claires ou jaunâtres avec halo rouge sur mains et pieds. Lésions maculo-papuleuses et vésiculeuses sur fesses et cuisses. Lésions érosives douloureuses de la bouche et lèvres. Chez la fille: possibles vésicules inguinales et vulvaires.',
      location: 'Mains, pieds, bouche, fesses'
    },
    complications: [
      'Rarement: myocardite, méningo-encéphalite, méningite aseptique'
    ],
    treatment: 'Traitement symptomatique',
    eviction: 'Selon état général et symptomatologie',
    severity: 'low'
  },
  {
    id: 'kawasaki',
    name: 'Maladie de Kawasaki',
    description: 'Vascularite aiguë d\'étiologie inconnue',
    icon: '💔',
    color: 'text-red-500 bg-red-500/10 border-red-500/30',
    incubation: 'Durée inconnue',
    contagion: 'Très faible contagiosité',
    symptoms: [
      'Fièvre élevée ≥5 jours',
      'Irritabilité',
      'Adénopathie cervicale',
      'Douleurs abdominales, diarrhée, vomissements',
      'Pyurie aseptique, dysfonction hépatique',
      'Yeux rouges (conjonctivite bulbaire non suppurative)',
      'Arthrite/arthralgies'
    ],
    rash: {
      type: 'Maculo-papulaire polymorphe',
      description: 'Éruption polymorphe, le plus souvent maculo-papulaire, morbilliforme ou scarlatiniforme, à tendance tronculaire. Rougeur et œdème induré des mains et pieds. Puis desquamation péri-unguéale et palmo-plantaire. Lèvres rouges et fissurées, langue framboisée.',
      location: 'Tronc, mains, pieds, lèvres'
    },
    complications: [
      'Anévrismes coronariens',
      'Infarctus du myocarde',
      'Arythmies',
      'Péricardite',
      'Méningite aseptique'
    ],
    treatment: 'Immunoglobulines IV (IVIG) + Aspirine',
    eviction: 'Éviction jusqu\'à guérison',
    severity: 'critical',
    warning: 'URGENCE - Risque d\'atteinte cardiaque. Hospitalisation obligatoire.'
  },
  {
    id: 'purpura-meningococcemique',
    name: 'Purpura Méningococcémique',
    description: 'Infection par Neisseria meningitidis',
    icon: '🚨',
    color: 'text-red-600 bg-red-600/10 border-red-600/30',
    incubation: '1 à 10 jours',
    contagion: 'Jusqu\'à la fin du 1er jour d\'antibiothérapie',
    symptoms: [
      'Tableau septicémique: fièvre élevée, céphalées',
      'Douleurs abdominales, vomissements',
      'Adynamie, prostration, irritabilité',
      'Frissons, arthralgies, myalgies',
      'Purpura fulminans: purpura extensif, pâleur, cyanose',
      'Extrémités froides, collapsus, choc, coma',
      '⚠️ Tout purpura fébrile est a priori méningococcique'
    ],
    rash: {
      type: 'Purpurique',
      description: 'Éruption purpurique généralisée. Pétéchies puis ecchymoses. Généralisation rapide: élargissement et multiplication des taches purpuriques. Puis lésions bulleuses et plaques nécrotiques. Hémorragies muqueuses.',
      location: 'Généralisé'
    },
    complications: [
      'Méningite',
      'CIVD (Coagulation intravasculaire disséminée)',
      'Nécrose cutanée',
      'Gangrène des extrémités',
      'Arthrite',
      'Pneumonie',
      'Péricardite'
    ],
    treatment: 'URGENCE: Antibiothérapie IV immédiate + Réanimation pédiatrique',
    eviction: 'Éviction jusqu\'à guérison',
    severity: 'critical',
    warning: 'URGENCE VITALE - Appeler le 15 immédiatement. Ceftriaxone 50mg/kg IM/IV sans attendre.'
  },
  {
    id: 'gianotti-crosti',
    name: 'Maladie de Gianotti-Crosti',
    description: 'Éruption liée à diverses infections virales (EBV, Hépatite B...)',
    icon: '🔵',
    color: 'text-blue-400 bg-blue-400/10 border-blue-400/30',
    incubation: 'Variable selon l\'agent causal',
    contagion: 'Variable selon l\'agent pathogène',
    symptoms: [
      'État général conservé',
      'Peu ou pas de fièvre',
      'Peu ou pas de prurit',
      'Conjonctives normales'
    ],
    rash: {
      type: 'Papulaire',
      description: 'Papules roses ou rouges, parfois purpuriques, de petite taille. Localisation classique sur joues, coudes et genoux. Parfois grandes plaques rouges et surélevées par coalescence. Éruption souvent généralisée chez l\'enfant. Guérison sans cicatrices.',
      location: 'Joues, coudes, genoux'
    },
    complications: [
      'Généralement aucune'
    ],
    treatment: 'Traitement symptomatique',
    eviction: 'Généralement non indiquée',
    severity: 'low'
  }
];

export default function EruptiveDiseasesPage() {
  const { isAdult } = useAudience();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedDisease, setSelectedDisease] = useState(null);
  const [expandedSection, setExpandedSection] = useState('symptoms');

  const filteredDiseases = eruptiveDiseases.filter(disease =>
    disease.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    (disease.altName && disease.altName.toLowerCase().includes(searchQuery.toLowerCase())) ||
    disease.description.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const getSeverityBadge = (severity) => {
    switch (severity) {
      case 'critical':
        return <Badge className="bg-red-500/20 text-red-400 border-red-500/30">Urgence vitale</Badge>;
      case 'high':
        return <Badge className="bg-orange-500/20 text-orange-400 border-orange-500/30">Sévère</Badge>;
      case 'medium':
        return <Badge className="bg-amber-500/20 text-amber-400 border-amber-500/30">Modéré</Badge>;
      default:
        return <Badge className="bg-green-500/20 text-green-400 border-green-500/30">Bénin</Badge>;
    }
  };

  return (
    <div className="p-4 md:p-6 space-y-6">
      {/* Header */}
      <div className="text-center mb-8">
        <h1 className="text-3xl font-bold flex items-center justify-center gap-3 mb-2">
          <div className="p-2 rounded-xl bg-gradient-to-br from-pink-500/20 to-rose-500/20 border border-pink-500/30">
            <Baby className="w-8 h-8 text-pink-400" />
          </div>
          Maladies Éruptives de l'Enfant
        </h1>
        <p className="text-[hsl(var(--text-dim))]">
          Guide diagnostic des éruptions fébriles pédiatriques
        </p>
      </div>

      {/* Search */}
      <div className="max-w-2xl mx-auto">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[hsl(var(--muted))]" />
          <Input
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Rechercher une maladie (rougeole, varicelle, scarlatine...)"
            className="pl-10 bg-[hsl(var(--bg-elev))] border-[hsl(var(--border))]"
            data-testid="eruptive-search-input"
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery('')}
              className="absolute right-3 top-1/2 -translate-y-1/2"
            >
              <X className="w-4 h-4 text-[hsl(var(--muted))]" />
            </button>
          )}
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-6 max-w-7xl mx-auto">
        {/* Disease List */}
        <div className="lg:col-span-1 space-y-3">
          <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-pink-400" />
            Maladies ({filteredDiseases.length})
          </h2>
          <div className="space-y-2 max-h-[600px] overflow-y-auto pr-2">
            {filteredDiseases.map((disease) => (
              <button
                key={disease.id}
                onClick={() => setSelectedDisease(disease)}
                className={`w-full text-left p-4 rounded-xl border transition-all ${
                  selectedDisease?.id === disease.id
                    ? `${disease.color} border-2`
                    : 'bg-[hsl(var(--bg-elev))] border-[hsl(var(--border))] hover:border-pink-400/30'
                }`}
                data-testid={`disease-item-${disease.id}`}
              >
                <div className="flex items-center gap-3">
                  <span className="text-2xl">{disease.icon}</span>
                  <div className="flex-1">
                    <p className="font-medium">{disease.name}</p>
                    {disease.altName && (
                      <p className="text-xs text-[hsl(var(--text-dim))]">{disease.altName}</p>
                    )}
                  </div>
                  {getSeverityBadge(disease.severity)}
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Disease Details */}
        <div className="lg:col-span-2">
          {selectedDisease ? (
            <div className="glass-card rounded-2xl p-6 border border-[hsl(var(--border))] space-y-6 animate-scale-in">
              {/* Header */}
              <div className="flex items-start gap-4">
                <span className="text-4xl">{selectedDisease.icon}</span>
                <div className="flex-1">
                  <h2 className="text-2xl font-bold">{selectedDisease.name}</h2>
                  {selectedDisease.altName && (
                    <p className="text-[hsl(var(--text-dim))]">{selectedDisease.altName}</p>
                  )}
                  <p className="text-sm text-[hsl(var(--text-dim))] mt-1">{selectedDisease.description}</p>
                  <div className="mt-2">{getSeverityBadge(selectedDisease.severity)}</div>
                </div>
              </div>

              {/* Warning if exists */}
              {selectedDisease.warning && (
                <div className="p-4 rounded-xl bg-red-500/10 border border-red-500/30">
                  <div className="flex items-start gap-3">
                    <AlertTriangle className="w-6 h-6 text-red-400 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-bold text-red-400">⚠️ ATTENTION</p>
                      <p className="text-sm text-red-300">{selectedDisease.warning}</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Key Info Cards */}
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="p-4 rounded-xl bg-[hsl(var(--bg-elev))] border border-[hsl(var(--border))]">
                  <div className="flex items-center gap-2 text-cyan-400 mb-2">
                    <Clock className="w-4 h-4" />
                    <span className="text-sm font-medium">Incubation</span>
                  </div>
                  <p className="text-sm">{selectedDisease.incubation}</p>
                </div>
                <div className="p-4 rounded-xl bg-[hsl(var(--bg-elev))] border border-[hsl(var(--border))]">
                  <div className="flex items-center gap-2 text-amber-400 mb-2">
                    <Users className="w-4 h-4" />
                    <span className="text-sm font-medium">Contagiosité</span>
                  </div>
                  <p className="text-sm">{selectedDisease.contagion}</p>
                </div>
              </div>

              {/* Expandable Sections */}
              <div className="space-y-3">
                {/* Symptoms */}
                <div className="border border-[hsl(var(--border))] rounded-xl overflow-hidden">
                  <button
                    onClick={() => setExpandedSection(expandedSection === 'symptoms' ? '' : 'symptoms')}
                    className="w-full p-4 flex items-center justify-between bg-[hsl(var(--bg-elev))] hover:bg-[hsl(var(--bg-elev))]/80"
                  >
                    <div className="flex items-center gap-2">
                      <Thermometer className="w-5 h-5 text-orange-400" />
                      <span className="font-medium">Signes cliniques</span>
                    </div>
                    {expandedSection === 'symptoms' ? <ChevronDown className="w-5 h-5" /> : <ChevronRight className="w-5 h-5" />}
                  </button>
                  {expandedSection === 'symptoms' && (
                    <div className="p-4 space-y-2">
                      {selectedDisease.symptoms.map((symptom, i) => (
                        <div key={i} className="flex items-start gap-2 text-sm">
                          <span className="text-orange-400 mt-1">•</span>
                          <span className={symptom.startsWith('⚠️') ? 'text-amber-400 font-medium' : ''}>{symptom}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Rash */}
                <div className="border border-[hsl(var(--border))] rounded-xl overflow-hidden">
                  <button
                    onClick={() => setExpandedSection(expandedSection === 'rash' ? '' : 'rash')}
                    className="w-full p-4 flex items-center justify-between bg-[hsl(var(--bg-elev))] hover:bg-[hsl(var(--bg-elev))]/80"
                  >
                    <div className="flex items-center gap-2">
                      <Eye className="w-5 h-5 text-pink-400" />
                      <span className="font-medium">Éruption cutanée</span>
                      <Badge variant="outline" className="ml-2">{selectedDisease.rash.type}</Badge>
                    </div>
                    {expandedSection === 'rash' ? <ChevronDown className="w-5 h-5" /> : <ChevronRight className="w-5 h-5" />}
                  </button>
                  {expandedSection === 'rash' && (
                    <div className="p-4 space-y-3">
                      <p className="text-sm">{selectedDisease.rash.description}</p>
                      <div className="flex items-center gap-2 text-sm text-cyan-400">
                        <Activity className="w-4 h-4" />
                        <span>Localisation: {selectedDisease.rash.location}</span>
                      </div>
                    </div>
                  )}
                </div>

                {/* Complications */}
                <div className="border border-[hsl(var(--border))] rounded-xl overflow-hidden">
                  <button
                    onClick={() => setExpandedSection(expandedSection === 'complications' ? '' : 'complications')}
                    className="w-full p-4 flex items-center justify-between bg-[hsl(var(--bg-elev))] hover:bg-[hsl(var(--bg-elev))]/80"
                  >
                    <div className="flex items-center gap-2">
                      <AlertTriangle className="w-5 h-5 text-red-400" />
                      <span className="font-medium">Complications</span>
                    </div>
                    {expandedSection === 'complications' ? <ChevronDown className="w-5 h-5" /> : <ChevronRight className="w-5 h-5" />}
                  </button>
                  {expandedSection === 'complications' && (
                    <div className="p-4">
                      <div className="flex flex-wrap gap-2">
                        {selectedDisease.complications.map((comp, i) => (
                          <Badge key={i} variant="outline" className="bg-red-500/10 text-red-300 border-red-500/30">
                            {comp}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {/* Treatment */}
                <div className="border border-[hsl(var(--border))] rounded-xl overflow-hidden">
                  <button
                    onClick={() => setExpandedSection(expandedSection === 'treatment' ? '' : 'treatment')}
                    className="w-full p-4 flex items-center justify-between bg-[hsl(var(--bg-elev))] hover:bg-[hsl(var(--bg-elev))]/80"
                  >
                    <div className="flex items-center gap-2">
                      <Pill className="w-5 h-5 text-green-400" />
                      <span className="font-medium">Traitement</span>
                    </div>
                    {expandedSection === 'treatment' ? <ChevronDown className="w-5 h-5" /> : <ChevronRight className="w-5 h-5" />}
                  </button>
                  {expandedSection === 'treatment' && (
                    <div className="p-4">
                      <p className="text-sm">{selectedDisease.treatment}</p>
                    </div>
                  )}
                </div>

                {/* Eviction */}
                <div className="p-4 rounded-xl bg-purple-500/10 border border-purple-500/30">
                  <div className="flex items-center gap-2 text-purple-400 mb-2">
                    <Users className="w-4 h-4" />
                    <span className="font-medium">Éviction scolaire</span>
                  </div>
                  <p className="text-sm">{selectedDisease.eviction}</p>
                </div>
              </div>
            </div>
          ) : (
            <div className="glass-card rounded-2xl p-12 border border-[hsl(var(--border))] flex flex-col items-center justify-center text-center h-full">
              <div className="w-20 h-20 rounded-full bg-pink-500/10 flex items-center justify-center mb-4">
                <Baby className="w-10 h-10 text-pink-400" />
              </div>
              <h3 className="text-lg font-medium mb-2">Sélectionnez une maladie</h3>
              <p className="text-sm text-[hsl(var(--text-dim))] max-w-md">
                Cliquez sur une maladie éruptive pour voir ses caractéristiques cliniques, 
                son éruption, ses complications et son traitement.
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Disclaimer */}
      <div className="max-w-7xl mx-auto">
        <div className="glass-card rounded-lg p-4 border border-amber-500/30">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-amber-400">Information importante</p>
              <p className="text-xs text-[hsl(var(--text-dim))] mt-1">
                Ce guide est une aide à l'orientation diagnostique. Le diagnostic définitif et la prise en charge 
                doivent être réalisés par un médecin. En cas de purpura fébrile, appeler le 15 immédiatement.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
